import { useState, useMemo, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogHero from './components/BlogHero';
import CategoryFilter from './components/CategoryFilter';
import ArticleCard from './components/ArticleCard';
import BlogSEO from './components/BlogSEO';
import { initBlogStore, getAllPosts, type BlogArticle } from '@/hooks/useBlogStore';
import { blogCategories, articles as mockArticles } from '@/mocks/blog';

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('all');
  // Show mock articles immediately — no blank loading state
  const [posts, setPosts] = useState<BlogArticle[]>(mockArticles);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        await initBlogStore();
        const all = await getAllPosts();
        if (!cancelled) setPosts(all);
      } catch (err) {
        console.error('Failed to load blog posts from DB:', err);
        // mockArticles already in state, nothing to do
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const filteredArticles = useMemo(() => {
    if (activeCategory === 'all') return posts;
    return posts.filter((a) => a.category === activeCategory);
  }, [posts, activeCategory]);

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'ClearMind Club Blog',
    description: 'Science-backed articles on mind training, nootropics, productivity, and wellness routines for a sharper, calmer mind.',
    url: 'https://clearmind.club/blog',
    blogPost: posts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      url: `https://clearmind.club/blog/${post.slug}`,
      datePublished: post.date,
      image: post.image,
      author: { '@type': 'Person', name: post.author },
    })),
  };

  const newsletterSubmitAddr = 'https://readdy.ai/api/form/d9385ptmi650so75doqg';

  return (
    <div className="min-h-screen bg-background-50">
      <BlogSEO
        pageTitle="Blog | Daily Tips for Cognitive Wellness"
        pageDescription="Explore science-backed articles on mind training, nootropics, productivity, and wellness routines. Practical tips for a sharper, calmer mind — no jargon."
        canonicalPath="/blog"
        ogType="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }}
      />
      <Navbar />
      <main>
        <BlogHero />

        {/* Category Filter */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-8 bg-background-50 border-b border-background-200/70 sticky top-[73px] z-30 backdrop-blur-md bg-background-50/90">
          <div className="max-w-6xl mx-auto">
            <CategoryFilter
              categories={blogCategories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </div>
        </section>

        {/* Articles Grid */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            {filteredArticles.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article, index) => (
                  <div
                    key={article.id}
                    style={{ animationDelay: `${index * 80}ms` }}
                  >
                    <ArticleCard article={article} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-file-search-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-600 font-body">No articles found in this category yet.</p>
                <button
                  type="button"
                  onClick={() => setActiveCategory('all')}
                  className="mt-3 text-sm font-label font-medium text-secondary-500 hover:text-secondary-600 transition-colors cursor-pointer"
                >
                  View all posts
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-16 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
              <i className="ri-mail-send-line text-accent-700 text-lg" />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3 text-primary-500">
              Get Weekly Tips Straight to Your Inbox
            </h2>
            <p className="text-foreground-600 mb-6 font-body leading-relaxed">
              One email per week with our best articles, simple routines, and practical cognitive wellness tips. No spam, unsubscribe anytime.
            </p>

            <form
              action={newsletterSubmitAddr}
              method="POST"
              data-readdy-form=""
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              onSubmit={(e) => {
                const form = e.currentTarget;
                const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
                if (honeypot && honeypot.value.trim()) {
                  e.preventDefault();
                  form.reset();
                  const msgEl = form.querySelector<HTMLDivElement>('.form-status');
                  if (msgEl) {
                    msgEl.textContent = 'Thanks for subscribing!';
                    msgEl.classList.remove('text-red-600');
                    msgEl.classList.add('text-accent-700');
                  }
                  return;
                }
                e.preventDefault();
                const formData = new FormData(form);
                fetch(newsletterSubmitAddr, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                  body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                })
                  .then(async (res) => {
                    const text = await res.text();
                    let parsed;
                    try { parsed = JSON.parse(text); } catch { parsed = null; }
                    const msgEl = form.querySelector<HTMLDivElement>('.form-status');
                    if (res.ok && parsed?.code === 'OK') {
                      if (msgEl) {
                        msgEl.textContent = 'Welcome aboard! Check your inbox for your first tip.';
                        msgEl.classList.remove('text-red-600');
                        msgEl.classList.add('text-accent-700');
                      }
                      form.reset();
                    } else {
                      const serverMsg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
                      if (msgEl) {
                        msgEl.textContent = serverMsg;
                        msgEl.classList.remove('text-accent-700');
                        msgEl.classList.add('text-red-600');
                      }
                    }
                  })
                  .catch(() => {
                    const msgEl = form.querySelector<HTMLDivElement>('.form-status');
                    if (msgEl) {
                      msgEl.textContent = 'Network error. Please try again.';
                      msgEl.classList.remove('text-accent-700');
                      msgEl.classList.add('text-red-600');
                    }
                  });
              }}
            >
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                className="flex-1 h-11 rounded-lg border border-background-200/70 bg-background-50 px-4 text-sm font-body text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-transparent"
              />
              <button
                type="submit"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 bg-accent-500 text-foreground-950 hover:bg-accent-700 h-11 rounded-lg text-sm px-6 cursor-pointer"
              >
                Subscribe
                <i className="ri-arrow-right-line" />
              </button>
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
              <div className="form-status text-sm font-label mt-1 w-full" />
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}